/**
 * WAV (RIFF/WAVE) reader and writer. Keeps the native sample rate and reports the
 * original bit depth (PLAN.md §5.1). Supports PCM 8/16/24/32-bit, IEEE float 32/64,
 * and WAVE_FORMAT_EXTENSIBLE.
 *
 * Integer PCM is scaled by 2^(bits−1): reading divides, writing multiplies, rounds
 * and clips to [−2^(bits−1), 2^(bits−1) − 1]. Reading a written file and writing it
 * again therefore reproduces it bit-exactly.
 */

export type WavEncoding = 'pcm16' | 'pcm24' | 'float32';

export interface WavInfo {
  sampleRate: number;
  channels: Float32Array[];
  bitsPerSample: number;
  format: 'pcm' | 'float';
}

const FMT_PCM = 1;
const FMT_FLOAT = 3;
const FMT_EXTENSIBLE = 0xfffe;

export class WavError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'WavError';
  }
}

function fourcc(v: DataView, o: number): string {
  return String.fromCharCode(
    v.getUint8(o),
    v.getUint8(o + 1),
    v.getUint8(o + 2),
    v.getUint8(o + 3),
  );
}

export function isWav(bytes: ArrayBuffer | Uint8Array): boolean {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (u.length < 12) return false;
  const s = (o: number) => String.fromCharCode(u[o]!, u[o + 1]!, u[o + 2]!, u[o + 3]!);
  return (s(0) === 'RIFF' || s(0) === 'RF64') && s(8) === 'WAVE';
}

/** Parse only the header: sample rate, channels, frame count (for length checks before decoding). */
export function readWavHeader(bytes: ArrayBuffer | Uint8Array): {
  sampleRate: number;
  numChannels: number;
  bitsPerSample: number;
  format: 'pcm' | 'float';
  frames: number;
  dataOffset: number;
  dataBytes: number;
} {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const v = new DataView(u.buffer, u.byteOffset, u.byteLength);
  if (!isWav(u)) throw new WavError('Not a WAV file (missing RIFF/WAVE header).');
  let o = 12;
  let fmt: { tag: number; ch: number; rate: number; bits: number; align: number } | null = null;
  while (o + 8 <= u.length) {
    const id = fourcc(v, o);
    let size = v.getUint32(o + 4, true);
    const body = o + 8;
    if (id === 'fmt ') {
      let tag = v.getUint16(body, true);
      const ch = v.getUint16(body + 2, true);
      const rate = v.getUint32(body + 4, true);
      const align = v.getUint16(body + 12, true);
      const bits = v.getUint16(body + 14, true);
      if (tag === FMT_EXTENSIBLE && size >= 40) tag = v.getUint16(body + 24, true);
      fmt = { tag, ch, rate, bits, align };
    } else if (id === 'data') {
      if (!fmt) throw new WavError('WAV data chunk appears before the fmt chunk.');
      // Streaming writers may leave the size as 0 or 0xFFFFFFFF: use the rest of the file.
      if (size === 0 || size === 0xffffffff || body + size > u.length) size = u.length - body;
      if (fmt.tag !== FMT_PCM && fmt.tag !== FMT_FLOAT) {
        throw new WavError(`Unsupported WAV encoding (format tag ${fmt.tag}).`);
      }
      if (fmt.tag === FMT_PCM && ![8, 16, 24, 32].includes(fmt.bits)) {
        throw new WavError(`Unsupported PCM bit depth ${fmt.bits}.`);
      }
      if (fmt.tag === FMT_FLOAT && ![32, 64].includes(fmt.bits)) {
        throw new WavError(`Unsupported float bit depth ${fmt.bits}.`);
      }
      if (fmt.ch < 1) throw new WavError('WAV file has no channels.');
      const bytesPerFrame = fmt.align || (fmt.ch * fmt.bits) / 8;
      return {
        sampleRate: fmt.rate,
        numChannels: fmt.ch,
        bitsPerSample: fmt.bits,
        format: fmt.tag === FMT_FLOAT ? 'float' : 'pcm',
        frames: Math.floor(size / bytesPerFrame),
        dataOffset: body,
        dataBytes: size,
      };
    }
    o = body + size + (size & 1);
  }
  throw new WavError('WAV file has no data chunk.');
}

export function readWav(bytes: ArrayBuffer | Uint8Array, maxFrames = Infinity): WavInfo {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const h = readWavHeader(u);
  const v = new DataView(u.buffer, u.byteOffset, u.byteLength);
  const frames = Math.min(h.frames, maxFrames);
  const C = h.numChannels;
  const bps = h.bitsPerSample / 8;
  const channels = Array.from({ length: C }, () => new Float32Array(frames));
  let o = h.dataOffset;
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < C; c++) {
      let s: number;
      if (h.format === 'float') {
        s = bps === 4 ? v.getFloat32(o, true) : v.getFloat64(o, true);
      } else if (bps === 1) {
        s = (v.getUint8(o) - 128) / 128;
      } else if (bps === 2) {
        s = v.getInt16(o, true) / 32768;
      } else if (bps === 3) {
        const b = v.getUint8(o) | (v.getUint8(o + 1) << 8) | (v.getInt8(o + 2) << 16);
        s = b / 8388608;
      } else {
        s = v.getInt32(o, true) / 2147483648;
      }
      channels[c]![i] = s;
      o += bps;
    }
  }
  return { sampleRate: h.sampleRate, channels, bitsPerSample: h.bitsPerSample, format: h.format };
}

export function writeWav(
  channels: Float32Array[],
  sampleRate: number,
  encoding: WavEncoding = 'pcm16',
): Uint8Array {
  const C = channels.length;
  if (C < 1) throw new WavError('No channels to write.');
  const frames = channels[0]!.length;
  const bits = encoding === 'pcm16' ? 16 : encoding === 'pcm24' ? 24 : 32;
  const bps = bits / 8;
  const dataBytes = frames * C * bps;
  const buf = new Uint8Array(44 + dataBytes + (dataBytes & 1));
  const v = new DataView(buf.buffer);
  const str = (o: number, s: string) => {
    for (let i = 0; i < 4; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  str(0, 'RIFF');
  v.setUint32(4, buf.length - 8, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, encoding === 'float32' ? FMT_FLOAT : FMT_PCM, true);
  v.setUint16(22, C, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * C * bps, true);
  v.setUint16(32, C * bps, true);
  v.setUint16(34, bits, true);
  str(36, 'data');
  v.setUint32(40, dataBytes, true);
  let o = 44;
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < C; c++) {
      const x = channels[c]![i]!;
      if (encoding === 'float32') {
        v.setFloat32(o, x, true);
      } else if (encoding === 'pcm16') {
        v.setInt16(o, Math.max(-32768, Math.min(32767, Math.round(x * 32768))), true);
      } else {
        const s = Math.max(-8388608, Math.min(8388607, Math.round(x * 8388608)));
        v.setUint8(o, s & 0xff);
        v.setUint8(o + 1, (s >> 8) & 0xff);
        v.setUint8(o + 2, (s >> 16) & 0xff);
      }
      o += bps;
    }
  }
  return buf;
}
